import React, { useCallback, useRef } from 'react';
import { RadioChangeEvent } from 'antd';
import { useTranslation } from 'react-i18next';

import TypedStepModal, {
  TypedStepModalStep,
  TypedStepTypeSelection,
  useTypedStepModalState,
} from '../../../../components/common/TypedStepModal';
import { SECTION_CLASS_NAME } from '../../../../components/common/TypedStepModal/const';
import {
  EXTERNAL_MASKING_SCHEMA,
  EXTERNAL_MASKING_SCHEMA_FILE_NAME,
} from '../../../../components/Settings/externalMaskingSchema';
import { useValidationForm } from '../../../../utils/validationUtil';
import {
  createEmptyDetectorStep,
  getDetectorTypeDescription,
  getDetectorTypeIconName,
  getDetectorTypeTitle,
  GuardrailDetectorSettings,
  IMaskingDetectorStep,
  MaskingDetectorTypeValue,
  normalizeDetectorSettings,
} from '../shared/utils';
import { donwloadAsNamedJsonFile } from '../../../../utils/fileUtil';

import {
  buildDetectorValidationForm,
  detectorFieldValueFnMap,
  DetectorValidationField,
  getFirstInvalidDetectorField,
} from './DetectorModal.validation';
import ExternalDetectorParameters from './ExternalDetectorParameters';
import RegexDetectorParameters from './RegexDetectorParameters';

const DRAFT_DETECTOR_KEY = '__draft_detector__';
const MODAL_WIDTH = 960;

export interface IDetectorModalProps {
  isCreation?: boolean;
  step?: IMaskingDetectorStep;
  visible: boolean;
  onClose: () => void;
  onSave: (step: IMaskingDetectorStep) => Promise<void>;
}

const DetectorModal: React.FC<IDetectorModalProps> = ({ isCreation = false, step, visible, onClose, onSave }) => {
  const { t } = useTranslation();
  const validationForm = useValidationForm(buildDetectorValidationForm(step), detectorFieldValueFnMap);
  const { draftStep, modalStep, saving, setDraftStep, setModalStep, setSaving } = useTypedStepModalState<
    IMaskingDetectorStep
  >({
    isCreation,
    step,
    visible,
    onReset: (nextStep) => validationForm.reset(buildDetectorValidationForm(nextStep)),
  });
  const apiUrlFieldRef = useRef<HTMLDivElement>(null);
  const patternsFieldRef = useRef<HTMLDivElement>(null);
  const customOptionsFieldRef = useRef<HTMLDivElement>(null);

  const validationFieldRefs: Partial<Record<DetectorValidationField, React.RefObject<HTMLDivElement>>> = {
    apiUrl: apiUrlFieldRef,
    patterns: patternsFieldRef,
    customOptionsJson: customOptionsFieldRef,
  };

  const onDownloadSchema = useCallback(() => {
    donwloadAsNamedJsonFile(EXTERNAL_MASKING_SCHEMA_FILE_NAME, EXTERNAL_MASKING_SCHEMA);
  }, []);

  const onDetectorTypeChange = (event: RadioChangeEvent) => {
    const nextStep = createEmptyDetectorStep(DRAFT_DETECTOR_KEY, event.target.value as MaskingDetectorTypeValue);
    setDraftStep(nextStep);
    validationForm.reset(buildDetectorValidationForm(nextStep));
  };

  const typeSelectionOptions = [MaskingDetectorTypeValue.Regex, MaskingDetectorTypeValue.External].map((type) => {
    return {
      description: getDetectorTypeDescription(type),
      iconName: getDetectorTypeIconName(type),
      title: getDetectorTypeTitle(type),
      value: type,
    };
  });

  const updateDetector = (patch: Partial<GuardrailDetectorSettings>) => {
    if (!draftStep) {
      return;
    }

    const nextStep = {
      ...draftStep,
      detector: normalizeDetectorSettings({
        ...draftStep.detector,
        ...patch,
      } as GuardrailDetectorSettings),
    };

    setDraftStep(nextStep);
    validationForm.updateFormValues(buildDetectorValidationForm(nextStep));
  };

  const renderParameters = () => {
    if (!draftStep) {
      return null;
    }

    return (
      <div className={SECTION_CLASS_NAME}>
        {draftStep.detector.type === MaskingDetectorTypeValue.External && (
          <ExternalDetectorParameters
            step={draftStep}
            validationFieldRefs={validationFieldRefs}
            validationForm={validationForm}
            onChange={updateDetector}
            onDownloadSchema={onDownloadSchema}
          />
        )}
        {draftStep.detector.type === MaskingDetectorTypeValue.Regex && (
          <RegexDetectorParameters
            step={draftStep}
            validationFieldRefs={validationFieldRefs}
            validationForm={validationForm}
            onChange={updateDetector}
          />
        )}
      </div>
    );
  };

  const renderTypeSelection = () => (
    <TypedStepTypeSelection
      options={typeSelectionOptions}
      value={draftStep?.detector.type}
      onChange={onDetectorTypeChange}
    />
  );

  const onNextClick = () => {
    if (!draftStep) {
      return;
    }

    setModalStep(TypedStepModalStep.Parameters);
  };

  const onSaveClick = async () => {
    if (!draftStep) {
      return;
    }

    if (!validationForm.validate()) {
      const invalidField = getFirstInvalidDetectorField(buildDetectorValidationForm(draftStep));

      if (invalidField) {
        requestAnimationFrame(() =>
          validationFieldRefs[invalidField]?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        );
      }

      return;
    }

    setSaving(true);

    try {
      await onSave(draftStep);
    } finally {
      setSaving(false);
    }
  };

  const renderTitle = () => {
    if (modalStep === TypedStepModalStep.TypeSelection) {
      return t('Agent: Guardrails: Masking: Detector: Add');
    }

    return draftStep ? getDetectorTypeTitle(draftStep.detector.type) : null;
  };

  return (
    <TypedStepModal
      canGoNext={!!draftStep}
      modalStep={modalStep}
      parameters={renderParameters()}
      saving={saving}
      title={renderTitle()}
      typeSelection={renderTypeSelection()}
      visible={visible}
      width={MODAL_WIDTH}
      onClose={onClose}
      onNext={onNextClick}
      onSave={onSaveClick}
    />
  );
};

export default DetectorModal;
